import clsx from "clsx";
import { Crosshair } from "lucide-react";

import type { Phase, SubordinateUnit } from "../api/types";
import { CardSection, PropRow } from "../components/panel";
import { Badge, Button, FloatingPanel } from "../components/ui";
import { echelonName } from "../echelon";
import { unitDesignation } from "../i18n/designation";
import { k, t } from "../i18n/i18n";
import { unitMissionName } from "../i18n/vocabulary";
import { formatMgrs } from "../map/MapCanvas";
import { symbolSvg } from "../map/units/symbols";
import { useZoomLevels } from "../map/units/zoomLevels";
import { useUi } from "../state/ui";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { standingTaskLabel } from "./missionMenuModel";

/** Fiche d'une unité d'un niveau inférieur de la carte zoomée (registre J11) : lecture seule. Elle dit d'où vient sa
 *  position — la carte du dossier subordonné décliné, ou celle que la déclinaison lui donnerait — et sa mission à
 *  chaque temps. Elle ne se déplace pas ici : on déplace son chef (tout son sous-arbre suit) ou on ouvre son dossier. */

const PHASES: Phase[] = ["T1", "T2", "T3"];

const SIDE: Record<SubordinateUnit["side"], { label: string; tone: "friend" | "hostile" | "ok" }> = {
  AMI: { label: k("Ami"), tone: "friend" },
  ENI: { label: k("Ennemi"), tone: "hostile" },
  NEUTRE: { label: k("Neutre"), tone: "ok" },
};

export function SubUnitCard({ unitKey }: { unitKey: string }) {
  const units = useZoomLevels((s) => s.units);
  const phase = useUi((s) => s.phase);
  const setPhase = useUi((s) => s.setPhase);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const unit = units?.find((u) => u.key === unitKey);
  if (!unit) return null;
  const friendly = unit.side === "AMI";
  const pos = unit.positions[phase];
  // PC, trains de combat (A95) : même tâche à chaque temps, dite une fois ; chaque temps ne montre que le lieu.
  const standing = standingTaskLabel(unit);
  const origin =
    unit.source === "declined"
      ? t("Position de la carte de son dossier, « {name} ».", { name: unit.exercise_name ?? "" })
      : t("Position que lui donnerait l'ordre décliné de son chef (aucun dossier subordonné encore créé).");
  // Formation que rien ne détaille à ce niveau (registre J13) : réduite à un repère discret sur la carte.
  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader
      icon={
        <span
          role="img"
          aria-label={t("Symbole APP-6 de {unit}", { unit: unitDesignation(unit.designation) })}
          className="flex w-9 justify-center"
          dangerouslySetInnerHTML={{ __html: symbolSvg(unit.sidc, 26) }}
        />
      }
      title={<CardTitle title={unitDesignation(unit.designation)} subtitle={echelonName(unit.echelon, unit.arm, friendly) ?? undefined} />}
      actions={<CardActions />}
      footer={
        pos ? (
          <CardFooter>
            <Button variant="secondary" size="sm" onClick={() => requestFlyTo([pos.lon, pos.lat], 14)}>
              <Crosshair size={14} /> {t("Centrer")}
            </Button>
          </CardFooter>
        ) : undefined
      }
    >
      <div className="flex flex-wrap gap-1.5 px-4 pt-3 pb-2">
        <Badge tone={SIDE[unit.side].tone}>{t(SIDE[unit.side].label)}</Badge>
        <Badge>{t("Niveau inférieur — lecture seule")}</Badge>
      </div>
      <div className="px-4 pb-2">
        <PropRow label={t("Échelon")}>{echelonName(unit.echelon, unit.arm, friendly) ?? "—"}</PropRow>
        <PropRow label={t("Provenance")} className="items-start">
          <span className="text-left">{origin}</span>
        </PropRow>
        {unit.undetailed && (
          <PropRow label={t("Niveau affiché")} className="items-start">
            <span className="text-left">
              {t("Non détaillée à ce niveau : aucun ordre subordonné ne lui donne d'unités plus basses. Elle reste en repère discret.")}
            </span>
          </PropRow>
        )}
      </div>
      <CardSection title={standing ? t("Lieu par temps") : t("Mission par temps")}>
        {standing && <p className="px-2 pb-1.5 text-meta leading-snug text-ink-muted">{standing}</p>}
        <div className="space-y-1">
          {PHASES.map((p) => {
            const at = unit.positions[p];
            return (
              <button
                key={p}
                type="button"
                disabled={!at}
                onClick={() => {
                  setPhase(p);
                  if (at) requestFlyTo([at.lon, at.lat], 14);
                }}
                title={at ? t("Afficher {phase} et localiser l'unité", { phase: p }) : undefined}
                className={clsx(
                  "focus-inset flex min-h-8 w-full items-start gap-2.5 rounded-lg py-1.5 pr-1 pl-2 text-left transition-colors disabled:opacity-40",
                  p === phase ? "bg-selected" : at && "hover:bg-hover",
                )}
              >
                <span className={clsx("mt-px w-6 shrink-0 text-ui font-semibold tabular-nums", p === phase ? "text-ink" : "text-ink-muted")}>
                  {p}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-ui font-semibold text-ink">
                    {!at
                      ? t("Absente")
                      : standing
                        ? at.locality || t("Lieu-dit sans nom")
                        : unitMissionName(at.mission_label || at.mission) || t("Sans mission")}
                  </span>
                  {at && (
                    <span className="block truncate text-meta font-normal text-ink-muted">
                      {standing ? "" : `${at.locality || t("Lieu-dit sans nom")} · `}
                      {formatMgrs(at.lon, at.lat, 3)}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </CardSection>
      <p className="px-4 pt-1 pb-4 text-meta text-ink-muted">
        {t("Pour la déplacer, déplacez son chef sur la carte du dossier (ses subordonnés le suivent) ou ouvrez son dossier subordonné.")}
      </p>
    </FloatingPanel>
  );
}
